/**
 * Shared impersonation session helpers (localStorage = cross-tab lock, sessionStorage = mirror).
 * Used by layout, admin gate, and exit flows.
 */

import { supabase } from '../lib/supabase'
import { invokeAdminAction } from './supabaseFunctions'
import { isMasterStaffUsername, type StaffSessionLike } from './staffPermissionSync'
import { getWindowLocaleStorage } from './windowSessionStorage'

export const IMPERSONATION_UPDATED_EVENT = 'impersonation-updated'
export const IMPERSONATED_TENANT_ADMIN_ROLE = 'admin'

export type ImpersonationSource = 'control' | 'admin'

export type SetImpersonationResult = 'ok' | 'blocked' | 'same'

export type AuthorizedImpersonation = {
    tenantId: string
    source: ImpersonationSource
}

const impersonationAuthorizationCache = new Map<string, boolean>()

function normalizeRole(role: string | null | undefined): string {
    return (role || '').trim().toLowerCase()
}

function isPlatformAdminRole(role: string | null | undefined): boolean {
    const normalized = normalizeRole(role)
    return normalized === 'superadmin' || normalized === 'super_admin'
}

function isControlAccountImpersonatorRole(role: string | null | undefined): boolean {
    const normalized = normalizeRole(role)
    return ['admin', 'tenant_admin', 'manager', 'admin manager', 'owner'].includes(normalized)
}

function isStaffSessionSuperAdmin(staffSessionRaw: string | null | undefined): boolean {
    if (!staffSessionRaw) return false
    try {
        const session = JSON.parse(staffSessionRaw)
        return normalizeRole(session?.role) === 'superadmin'
    } catch {
        return false
    }
}

function getAuthorizationCacheKey(params: {
    tenantId: string
    source: ImpersonationSource
    profileTenantId?: string | null
    profileRole?: string | null
    hasStaffSession?: boolean
}): string {
    return [
        params.source,
        params.tenantId,
        params.profileTenantId || '',
        normalizeRole(params.profileRole),
        params.hasStaffSession ? 'staff' : 'user',
    ].join(':')
}

function parseStaffSession(staffSessionRaw: string | null | undefined): StaffSessionLike | null {
    if (!staffSessionRaw) return null
    try {
        return JSON.parse(staffSessionRaw) as StaffSessionLike
    } catch {
        return null
    }
}

function canAdminSourceImpersonate(params: {
    profileRole?: string | null
    staffSessionRaw?: string | null
}): boolean {
    if (isPlatformAdminRole(params.profileRole)) return true

    const staff = parseStaffSession(params.staffSessionRaw)
    if (!staff) return true

    if (isMasterStaffUsername(staff.username) || normalizeRole((staff as { role?: string }).role) === 'superadmin') {
        return true
    }

    return Boolean(staff.allowImpersonate)
}

export async function assertCanStartImpersonation(params: {
    tenantId: string
    source: ImpersonationSource
    profileTenantId?: string | null
    profileRole?: string | null
    staffSessionRaw?: string | null
}): Promise<boolean> {
    const tenantId = parseImpersonationTenantId(params.tenantId)
    if (!tenantId) return false

    if (params.source === 'admin') {
        return canAdminSourceImpersonate(params)
    }

    if (!params.profileTenantId || !isControlAccountImpersonatorRole(params.profileRole)) {
        return false
    }

    const { data: parentTenant, error: parentError } = await supabase
        .from('tenants')
        .select('id, client_type')
        .eq('id', params.profileTenantId)
        .maybeSingle()

    if (parentError || parentTenant?.client_type !== 'Parent Client') {
        return false
    }

    return canControlAccountImpersonateTenant(params.profileTenantId, tenantId)
}

async function canControlAccountImpersonateTenant(masterTenantId: string, tenantId: string): Promise<boolean> {
    try {
        const { data } = await invokeAdminAction({
            action: 'get-child-clubs',
            masterId: masterTenantId,
        })
        if (Array.isArray(data)) {
            return data.some((club: { id?: unknown }) => String(club?.id || '') === tenantId)
        }
    } catch {
        // Fall through to direct query.
    }

    const { data: childTenant, error: childError } = await supabase
        .from('tenants')
        .select('id')
        .eq('id', tenantId)
        .eq('parent_tenant_id', masterTenantId)
        .maybeSingle()

    return !childError && childTenant?.id === tenantId
}

export function getEffectiveImpersonationRole(
    role: string | null | undefined,
    authorizedImpersonation: AuthorizedImpersonation | null
): string | null {
    if (authorizedImpersonation) return IMPERSONATED_TENANT_ADMIN_ROLE
    return role || null
}

export async function resolveAuthorizedImpersonation(params: {
    profileTenantId?: string | null
    profileRole?: string | null
    staffSessionRaw?: string | null
} = {}): Promise<AuthorizedImpersonation | null> {
    const tenantId = parseImpersonationTenantId(getImpersonateTenantRaw())
    const source = getImpersonationSource()
    if (!tenantId || !source) return null

    const cacheKey = getAuthorizationCacheKey({
        tenantId,
        source,
        profileTenantId: params.profileTenantId,
        profileRole: params.profileRole,
        hasStaffSession: !!params.staffSessionRaw,
    })
    const cached = impersonationAuthorizationCache.get(cacheKey)
    if (cached === true) {
        return { tenantId, source }
    }
    if (cached === false) {
        return null
    }

    if (source === 'admin') {
        const allowedClient = canAdminSourceImpersonate(params)
        if (!allowedClient) {
            impersonationAuthorizationCache.set(cacheKey, false)
            return null
        }
        try {
            const { data, error } = await invokeAdminAction({
                action: 'validate-impersonation',
                tenantId,
            })
            const payload = data as { allowed?: boolean } | null
            if (error || !payload?.allowed) {
                impersonationAuthorizationCache.set(cacheKey, false)
                return null
            }
        } catch {
            impersonationAuthorizationCache.set(cacheKey, false)
            return null
        }
        impersonationAuthorizationCache.set(cacheKey, true)
        return { tenantId, source }
    }

    if (!params.profileTenantId || !isControlAccountImpersonatorRole(params.profileRole)) {
        impersonationAuthorizationCache.set(cacheKey, false)
        return null
    }

    const { data: parentTenant, error: parentError } = await supabase
        .from('tenants')
        .select('id, client_type')
        .eq('id', params.profileTenantId)
        .maybeSingle()

    if (parentError || parentTenant?.client_type !== 'Parent Client') {
        impersonationAuthorizationCache.set(cacheKey, false)
        return null
    }

    const allowed = await canControlAccountImpersonateTenant(params.profileTenantId, tenantId)
    impersonationAuthorizationCache.set(cacheKey, allowed)
    return allowed ? { tenantId, source } : null
}

export function parseImpersonationTenantId(raw: string | null | undefined): string | null {
    if (!raw) return null
    const id = raw.split(':')[0]?.trim()
    return id || null
}

/** Staff session JSON without importing auth.service (avoids circular deps). */
export function findStaffSessionStrForImpersonation(): string | null {
    if (typeof window === 'undefined') return null
    const str =
        sessionStorage.getItem('staff_session') ||
        getWindowLocaleStorage('staff_session') ||
        localStorage.getItem('staff_session_shared') ||
        localStorage.getItem('staff_session')
    if (str) return str
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i)
            if (!k || !k.startsWith('staff_session_')) continue
            const v = localStorage.getItem(k)
            if (v && v.includes('"role"')) return v
        }
    } catch {
        /* ignore */
    }
    return null
}

/** True when staff or authorized impersonation storage is active (no Supabase user required). */
export function isImpersonationSession(): boolean {
    if (typeof window === 'undefined') return false
    const staff = findStaffSessionStrForImpersonation()
    const raw = getImpersonateTenantRaw()
    return !!(staff || (raw && raw.includes('-')))
}

/** Cross-tab source of truth — localStorage wins over per-tab sessionStorage. */
export function getImpersonateTenantRaw(): string | null {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('impersonate_tenant') || sessionStorage.getItem('impersonate_tenant')
}

export function getImpersonationTenantName(): string | null {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('impersonate_tenant_name') || sessionStorage.getItem('impersonate_tenant_name')
}

export function getImpersonationSource(): ImpersonationSource | null {
    if (typeof window === 'undefined') return null
    const a = localStorage.getItem('impersonate_source')
    const b = sessionStorage.getItem('impersonate_source')
    const c = sessionStorage.getItem('impersonate_tenant_source')
    for (const s of [a, b, c]) {
        if (s === 'control' || s === 'admin') return s
    }
    return null
}

export function getActiveImpersonation(): {
    id: string | null
    name: string | null
    source: ImpersonationSource | null
} {
    const raw = getImpersonateTenantRaw()
    return {
        id: parseImpersonationTenantId(raw),
        name: getImpersonationTenantName(),
        source: getImpersonationSource(),
    }
}

export function isImpersonationActive(): boolean {
    return !!getActiveImpersonation().id
}

/** True when tenant-scoped data must use admin-actions (staff JWT or child-tenant impersonation). */
export function shouldBypassTenantRls(): boolean {
    if (isImpersonationActive()) return true
    return !!findStaffSessionStrForImpersonation()
}

export function getImpersonationLockedTooltip(): string {
    const { name } = getActiveImpersonation()
    return `Currently impersonating ${name || 'an account'}. Exit impersonation to switch.`
}

/** Mirror localStorage impersonation into sessionStorage for this tab. */
export function syncImpersonationSessionFromLocalStorage(): void {
    if (typeof window === 'undefined') return
    try {
        const tenant = localStorage.getItem('impersonate_tenant')
        const name = localStorage.getItem('impersonate_tenant_name')
        const source = localStorage.getItem('impersonate_source')

        if (tenant) {
            sessionStorage.setItem('impersonate_tenant', tenant)
            if (name) sessionStorage.setItem('impersonate_tenant_name', name)
            if (source === 'admin' || source === 'control') {
                sessionStorage.setItem('impersonate_source', source)
            }
            return
        }

        sessionStorage.removeItem('impersonate_tenant')
        sessionStorage.removeItem('impersonate_tenant_name')
        sessionStorage.removeItem('impersonate_source')
        sessionStorage.removeItem('impersonate_tenant_source')
    } catch {
        /* ignore */
    }
}

function dispatchImpersonationUpdated(): void {
    if (typeof window === 'undefined') return
    try {
        // Use a dedicated event — generic `storage` (especially keyless synthetic events)
        // caused permission/layout reload loops in the same tab.
        window.dispatchEvent(new Event(IMPERSONATION_UPDATED_EVENT))
    } catch {
        /* ignore */
    }
}

/**
 * Start or refresh impersonation. Only one tenant may be impersonated at a time (localStorage lock).
 * Returns `blocked` when another tenant is already active in any tab.
 */
export function setImpersonationStorage(params: {
    tenantId: string
    tenantName: string
    source: ImpersonationSource
}): SetImpersonationResult {
    if (typeof window === 'undefined') return 'blocked'

    const nextId = parseImpersonationTenantId(params.tenantId)
    if (!nextId) return 'blocked'

    const existingId = parseImpersonationTenantId(localStorage.getItem('impersonate_tenant'))
    if (existingId && existingId !== nextId) {
        return 'blocked'
    }
    if (existingId === nextId) {
        syncImpersonationSessionFromLocalStorage()
        return 'same'
    }

    try {
        localStorage.setItem('impersonate_tenant', params.tenantId)
        localStorage.setItem('impersonate_tenant_name', params.tenantName)
        localStorage.setItem('impersonate_source', params.source)

        sessionStorage.setItem('impersonate_tenant', params.tenantId)
        sessionStorage.setItem('impersonate_tenant_name', params.tenantName)
        sessionStorage.setItem('impersonate_source', params.source)
    } catch {
        return 'blocked'
    }

    dispatchImpersonationUpdated()
    return 'ok'
}

/**
 * Verify the caller may impersonate the target tenant before writing storage keys.
 */
export async function setAuthorizedImpersonationStorage(params: {
    tenantId: string
    tenantName: string
    source: ImpersonationSource
    profileTenantId?: string | null
    profileRole?: string | null
    staffSessionRaw?: string | null
}): Promise<SetImpersonationResult> {
    const allowed = await assertCanStartImpersonation({
        tenantId: params.tenantId,
        source: params.source,
        profileTenantId: params.profileTenantId,
        profileRole: params.profileRole,
        staffSessionRaw: params.staffSessionRaw,
    })
    if (!allowed) return 'blocked'

    return setImpersonationStorage({
        tenantId: params.tenantId,
        tenantName: params.tenantName,
        source: params.source,
    })
}

/** Target route after exit for control vs platform admin flows */
export function getImpersonationExitNavigatePath(): string {
    return getImpersonationExitPathForSource(getImpersonationSource())
}

export function getImpersonationExitPathForSource(source: ImpersonationSource | null): string {
    return source === 'control' ? '/control/dashboard' : '/admin/dashboard'
}

/** True when the route is only valid during an active impersonation session. */
export function isImpersonationViewPath(pathname: string, search: string): boolean {
    if (pathname.startsWith('/tenant') || pathname.startsWith('/control')) return true
    return false
}

/** Dashboard to return to while impersonation is still active (does not clear session). */
export function getImpersonationActiveDashboardPath(): string {
    if (getImpersonationSource() === 'control') {
        return '/control/dashboard'
    }

    return '/tenant/dashboard'
}

export function clearImpersonationStorage(): void {
    if (typeof window === 'undefined') return
    try {
        impersonationAuthorizationCache.clear()
        sessionStorage.removeItem('impersonate_tenant')
        localStorage.removeItem('impersonate_tenant')
        sessionStorage.removeItem('impersonate_tenant_name')
        localStorage.removeItem('impersonate_tenant_name')
        sessionStorage.removeItem('impersonate_source')
        localStorage.removeItem('impersonate_source')
        sessionStorage.removeItem('impersonate_tenant_source')
        dispatchImpersonationUpdated()
    } catch {
        /* ignore */
    }
}

/** Only `/admin/branding` is allowed while impersonating (child preview from control, etc.) */
export function isAdminPathAllowedDuringImpersonation(pathname: string): boolean {
    if (pathname === '/admin/branding') return true
    return pathname.startsWith('/admin/branding/')
}
